import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Download, Eye, History, CheckCircle2, Clock, Copy, FileText, Send } from 'lucide-react';
import { PdfPreviewDialog } from './PdfPreviewDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

/**
 * StatementVersionsDialog
 *
 * Standalone dialog that replicates the "Statement versions" card from
 * WrittenStatement Step 5.  Ownes its own data-fetching so it can be used
 * from the tenancy card in LandlordDashboard without any shared state.
 *
 * Props:
 *   open          – boolean
 *   onOpenChange  – (bool) => void
 *   statement     – the statement object returned by the dashboard API
 *                   (needs .id, .status, .document.pdf_path, .document.title_type)
 */
export const StatementVersionsDialog = ({ open, onOpenChange, statement, tenancyEnded = false }) => {
  const { user } = useAuth();

  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signingVersion, setSigningVersion] = useState(null);
  const [versionPreview, setVersionPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const ACTIVE_SIGNING = ['sent', 'partially_signed'];

  // Fetch versions and signing-status whenever the dialog opens
  useEffect(() => {
    if (!open || !statement?.id) return;
    setLoading(true);
    setSigningVersion(null);
    const versionsReq = axios
      .get(`${API_URL}/api/written-statements/${statement.id}/versions`, { withCredentials: true })
      .then((res) => setVersions(Array.isArray(res.data?.versions) ? res.data.versions : []))
      .catch(() => setVersions([]));
    // Always fetch signing-status — needed for 'draft+generated' case too
    const signingReq = axios
      .get(`${API_URL}/api/written-statements/${statement.id}/signing-status`, { withCredentials: true })
      .then((res) => setSigningVersion(res.data?.version || null))
      .catch(() => {});
    Promise.all([versionsReq, signingReq]).finally(() => setLoading(false));
  }, [open, statement?.id]);

  const handleSendForSigning = async () => {
    const ws = statement?.written_statement || {};
    const ll = ws.landlord || {};
    const isCompany = user?.ownership_type === 'company';
    const participants = [];

    // Landlord
    participants.push({
      role: 'landlord',
      signer_name: (ll.names || []).filter(Boolean).join(', ') || user?.name || '',
      email: ll.email || user?.email || '',
      entity_type: isCompany ? 'company' : 'individual',
      entity_name: isCompany ? (user?.company_name || '') : null,
      signer_role: isCompany ? (user?.director_name ? 'Director' : 'Authorised Signatory') : null,
      required: true,
    });

    // Tenants
    (ws.tenants || []).filter((t) => t.name && t.email).forEach((t) => {
      participants.push({
        role: 'tenant',
        user_id: t.user_id || null,
        signer_name: t.name,
        email: t.email,
        entity_type: 'individual',
        entity_name: null,
        signer_role: null,
        required: true,
      });
    });

    // Guarantors
    ((statement?.optional_terms?.guarantors) || []).filter((g) => g.name && g.email).forEach((g) => {
      participants.push({
        role: 'guarantor',
        signer_name: g.name,
        email: g.email,
        entity_type: 'individual',
        entity_name: null,
        signer_role: null,
        required: true,
      });
    });

    if (participants.length === 0) {
      toast.error('No valid participants to send to');
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/written-statements/${statement.id}/send`,
        { participants },
        { withCredentials: true }
      );
      toast.success('Signing invites sent successfully');
      onOpenChange(false);
      if (onSent) onSent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to send for signing';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const downloadCurrent = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/written-statements/${statement.id}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `written_statement_${statement.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const viewVersionPdf = async (version) => {
    setVersionPreview({ pdfData: null, sourceKey: `v-${version.id}`, errorMessage: '', versionNumber: version.version_number, loading: true });
    try {
      const res = await axios.get(
        `${API_URL}/api/written-statements/${statement.id}/versions/${version.id}/download`,
        { responseType: 'arraybuffer', withCredentials: true }
      );
      const bytes = new Uint8Array(res.data);
      setVersionPreview((prev) => prev ? { ...prev, pdfData: bytes, loading: false } : prev);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load version PDF';
      setVersionPreview((prev) => prev ? { ...prev, errorMessage: msg, loading: false } : prev);
    }
  };

  const downloadVersionPdf = async (version) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/written-statements/${statement.id}/versions/${version.id}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `written_statement_v${version.version_number || 'history'}_${statement.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download version');
    }
  };

  const status = statement?.status || 'draft';
  // hasPdf covers any historical version (for Download button visibility)
  const hasPdf = Boolean(
    statement?.document?.has_versions ||
    statement?.document?.download_available ||
    statement?.document?.final_pdf_path ||
    statement?.document?.pdf_path
  );
  // currentPdf: the current document has a PDF (required to send for signing).
  // Includes has_versions because the backend sets that flag on generation even
  // before download_available is confirmed by a file-existence check.
  const currentPdf = Boolean(
    statement?.document?.has_versions ||
    statement?.document?.download_available ||
    statement?.document?.final_pdf_path ||
    statement?.document?.pdf_path
  );
  // Statuses that allow sending for signing (pdf exists, not yet signed)
  const canSendForSigning = ['draft', 'generated'].includes(status) && currentPdf &&
    (!signingVersion || ['superseded', 'generated'].includes(signingVersion?.status));

  // Determine which version is "current" (non-superseded).
  // Treat 'generated' the same as non-draft for version display purposes.
  const isEffectivelyNonDraft = status !== 'draft';
  const currentVersion = isEffectivelyNonDraft
    ? (versions.find((v) => v.status !== 'superseded') || versions[0] || null)
    : null;
  const previousVersions = isEffectivelyNonDraft
    ? versions.filter((v) => v.id !== currentVersion?.id)
    : versions;

  const docTitle = statement?.document?.title_type === 'tenancy_agreement'
    ? 'Tenancy Agreement'
    : 'Written Statement';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <History className="h-4 w-4" />
              Statement versions
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Current statement row */}
            <div
              className={`rounded-lg border border-[#1e3a5f]/20 bg-blue-50/60 p-3 ${hasPdf ? 'cursor-pointer hover:bg-blue-100/60 transition-colors' : ''}`}
              onClick={() => { if (hasPdf) downloadCurrent(); }}
              title={hasPdf ? 'Download current statement' : undefined}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-[#1e3a5f]">
                    {tenancyEnded ? 'Latest version' : 'Current statement'}
                    {currentVersion?.version_number ? ` — Version ${currentVersion.version_number}` : ''}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {hasPdf ? `${docTitle} — click to download` : 'No PDF generated yet'}
                  </p>
                </div>
                <Badge
                  className={
                    (status === 'generated' || (status === 'draft' && hasPdf))
                      ? 'capitalize bg-green-100 text-green-800 border-green-300'
                      : 'capitalize'
                  }
                  variant={(status === 'generated' || (status === 'draft' && hasPdf)) ? undefined : 'outline'}
                >
                  {status === 'draft' && hasPdf ? 'generated' : status.replace('_', ' ')}
                </Badge>
              </div>
            </div>

            {/* Send for Signing — draft with PDF, not yet sent; hidden for ended tenancies */}
            {canSendForSigning && !tenancyEnded && (
              <button
                type="button"
                disabled={sending}
                className="w-full flex items-center justify-between rounded-lg border border-[#1e3a5f]/30 bg-[#1e3a5f]/5 hover:bg-[#1e3a5f]/10 transition-colors px-3 py-2.5 text-left disabled:opacity-50 disabled:pointer-events-none"
                onClick={handleSendForSigning}
              >
                <div>
                  <p className="text-sm font-semibold text-[#1e3a5f]">Send for Signing</p>
                  <p className="text-xs text-muted-foreground">Send signing invites to all parties</p>
                </div>
                {sending
                  ? <Loader2 className="h-4 w-4 text-[#1e3a5f] shrink-0 animate-spin" />
                  : <Send className="h-4 w-4 text-[#1e3a5f] shrink-0" />}
              </button>
            )}

            {/* Signatories — only when statement is actively awaiting signatures */}
            {signingVersion && ACTIVE_SIGNING.includes(signingVersion.status) && signingVersion.participants?.length > 0 && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Signatories</p>
                {signingVersion.participants.map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {p.signed
                        ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        : <Clock className="h-4 w-4 text-amber-400 shrink-0" />}
                      <span className="font-medium truncate">{p.signer_name}</span>
                      <Badge variant="outline" className="text-xs capitalize shrink-0">
                        {p.role}
                      </Badge>
                    </div>
                    {!p.signed && p.signing_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-xs h-7 px-2"
                        onClick={() => {
                          navigator.clipboard.writeText(p.signing_url);
                          toast.success(`Signing link copied for ${p.signer_name}`);
                        }}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy link
                      </Button>
                    )}
                    {p.signed && (
                      <span className="text-xs text-green-600 shrink-0">Signed</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Version history */}
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading version history…
              </div>
            ) : previousVersions.length > 0 ? (
              <details className="rounded-lg border p-3">
                <summary className="cursor-pointer text-sm font-medium text-[#1e3a5f]">
                  Previous versions ({previousVersions.length})
                </summary>
                <div className="mt-3 space-y-2">
                  {previousVersions.map((version) => {
                    const vHasPdf = Boolean(version.pdf_available);
                    return (
                      <div key={version.id} className="rounded-md border bg-white p-3 text-sm">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">Version {version.version_number || '—'}</span>
                              <Badge variant="outline" className="capitalize">
                                {(version.status || 'unknown').replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Created {fmtDate(version.created_at)}
                              {version.sent_at ? ` · Sent ${fmtDate(version.sent_at)}` : ''}
                              {version.completed_at ? ` · Signed ${fmtDate(version.completed_at)}` : ''}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!vHasPdf}
                              onClick={() => viewVersionPdf(version)}
                            >
                              <Eye className="h-3.5 w-3.5 mr-1.5" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!vHasPdf}
                              onClick={() => downloadVersionPdf(version)}
                            >
                              <Download className="h-3.5 w-3.5 mr-1.5" />
                              Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            ) : (
              <p className="text-sm text-muted-foreground">No previous versions yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Version PDF preview dialog */}
      <PdfPreviewDialog
        open={Boolean(versionPreview)}
        onOpenChange={(open) => { if (!open) setVersionPreview(null); }}
        title={
          <span className="flex min-w-0 items-center gap-2">
            <FileText className="h-5 w-5 shrink-0 text-royal-600" />
            <span className="truncate">
              {versionPreview?.versionNumber ? `Version ${versionPreview.versionNumber}` : 'Statement Version'}
            </span>
          </span>
        }
        description="Historical version of this written statement."
        pdfData={versionPreview?.pdfData}
        sourceKey={versionPreview?.sourceKey}
        loading={versionPreview?.loading}
        loadingTitle="Loading version PDF"
        errorMessage={versionPreview?.errorMessage}
        unavailableMessage="Version PDF unavailable."
      />
    </>
  );
};
